import React from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { apiPost, apiPut } from "../../utils/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { AdminFormDialog, AdminField } from "./AdminFormDialog";
import { Input, Textarea, SelectNative } from "../ui/input";
import { ImageUpload } from "../subcomponents/ImageUpload";
import { RichEditor } from "../subcomponents/RichEditor";
import { AdminMediaAttachment } from "./AdminMediaAttachment";
import type { MediaAttachmentLayout } from "../../utils/contentMedia";

export type ContentType = "article" | "event" | "activity";

const GALLERY_SLOTS = 15;

type ContentFormState = {
  title: string;
  excerpt: string;
  content: string;
  category: string;
  coverImage: string;
  galleryImages: string[];
  eventDate: string;
  location: string;
  specialProgramId: string;
  attachmentType: string;
  attachmentUrl: string;
  attachmentLayout: MediaAttachmentLayout;
};

const EMPTY_CONTENT: ContentFormState = {
  title: "",
  excerpt: "",
  content: "",
  category: "",
  coverImage: "",
  galleryImages: ["", "", "", ""],
  eventDate: "",
  location: "",
  specialProgramId: "",
  attachmentType: "",
  attachmentUrl: "",
  attachmentLayout: "auto",
};

const normalizeGallery = (raw: unknown): string[] => {
  const arr = Array.isArray(raw) ? raw.map((u) => String(u || "").trim()) : [];
  while (arr.length < GALLERY_SLOTS) arr.push("");
  return arr.slice(0, GALLERY_SLOTS);
};

type Props = {
  open: boolean;
  type: ContentType;
  editing: any | null;
  isAdmin: boolean;
  categories?: string[];
  specialPrograms?: any[];
  onClose: () => void;
  onSaved: () => void;
};

const FORM_ID = "content-form";

export const AdminContentModal: React.FC<Props> = ({ open, type, editing, isAdmin, categories = [], specialPrograms = [], onClose, onSaved }) => {
  const { t } = useLanguage();

  const categoryOptions = Array.from(
    new Set(categories.map((c) => (c || "").trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const typeLabels: Record<ContentType, string> = {
    article: t.admin.articles,
    event: t.admin.events,
    activity: t.admin.activities,
  };

  const folder =
    type === "event" ? "ulpgl/events" : type === "activity" ? "ulpgl/activities" : "ulpgl/articles";

  const schema = Yup.object({
    title: Yup.string().trim().required(t.common.required),
    content: Yup.string().trim().required(t.common.required),
    excerpt: Yup.string(),
    category: Yup.string(),
    coverImage: Yup.string(),
    eventDate: Yup.string(),
    location: Yup.string(),
  });

  const initial: ContentFormState = editing
    ? {
        title: editing.title || "",
        excerpt: editing.excerpt || "",
        content: editing.content || "",
        category: editing.category || "",
        coverImage: editing.coverImage || "",
        galleryImages: normalizeGallery(editing.galleryImages),
        eventDate: editing.eventDate ? editing.eventDate.slice(0, 16) : "",
        location: editing.location || "",
        specialProgramId: editing.specialProgramId ? String(editing.specialProgramId) : "",
        attachmentType: editing.attachmentType || "",
        attachmentUrl: editing.attachmentUrl || "",
        attachmentLayout: (editing.attachmentLayout as MediaAttachmentLayout) || "auto",
      }
    : EMPTY_CONTENT;

  const title = editing
    ? `${t.admin.edit} — ${typeLabels[type]}`
    : `${t.admin.create} — ${typeLabels[type]}`;

  const submitLabel = editing ? t.admin.save : isAdmin ? t.admin.publish : t.admin.submitForReview;

  return (
    <Formik
      initialValues={initial}
      validationSchema={schema}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        const payload = {
          title: values.title.trim(),
          excerpt: values.excerpt.trim(),
          content: values.content.trim(),
          category: values.category.trim(),
          coverImage: values.coverImage,
          galleryImages: values.galleryImages.map((u) => u.trim()).filter(Boolean),
          eventDate: values.eventDate || null,
          location: values.location.trim(),
          specialProgramId: values.specialProgramId ? Number(values.specialProgramId) : null,
          attachmentType: values.attachmentType || null,
          attachmentUrl: values.attachmentUrl.trim() || null,
          attachmentLayout: values.attachmentLayout || "auto",
        };
        try {
          if (editing) {
            await apiPut(`/contents/${editing.id}`, payload);
            toast.success(t.common.saveSuccess);
          } else {
            await apiPost("/contents", { ...payload, type });
            toast.success(isAdmin ? t.admin.published : t.admin.submitted);
          }
          onSaved();
          onClose();
        } catch (e: any) {
          toast.error(e?.response?.data?.reason || e?.messageText || t.common.error);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, isSubmitting, setFieldValue }) => (
        <AdminFormDialog
          open={open}
          onClose={onClose}
          title={title}
          subtitle={t.admin.pageContent}
          testId="content-modal"
          className="max-w-3xl"
          formId={FORM_ID}
          submitLabel={submitLabel}
          isSubmitting={isSubmitting}
        >
          <Form id={FORM_ID} className="grid gap-4 pb-4">
            <AdminField label={t.admin.contentTitle} required error={touched.title ? errors.title : undefined}>
              <Field as={Input} name="title" data-testid="form-title" />
            </AdminField>
            <AdminField label={t.admin.category}>
              <Field
                as={Input}
                name="category"
                list="content-category-options"
                data-testid="form-category"
                placeholder={t.admin.categoryPlaceholder}
                autoComplete="off"
              />
              <datalist id="content-category-options">
                {categoryOptions.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </AdminField>
            {specialPrograms.length > 0 && (
              <AdminField label={t.admin.specialProgram}>
                <Field as={SelectNative} name="specialProgramId" data-testid="form-special-program">
                  <option value="">{t.admin.selectSpecialProgram}</option>
                  {specialPrograms.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}{p.faculty?.name ? ` — ${p.faculty.name}` : ""}
                    </option>
                  ))}
                </Field>
              </AdminField>
            )}
            <ImageUpload
              value={values.coverImage}
              onChange={(url) => setFieldValue("coverImage", url)}
              folder={folder}
              label={t.admin.coverImage}
              testId="cover-image"
            />
            <div className="grid gap-3">
              <p className="text-sm font-medium m-0">{t.admin.galleryImages}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {values.galleryImages.map((url, idx) => (
                  <ImageUpload
                    key={`gallery-${idx}`}
                    value={url}
                    onChange={(u) => {
                      const next = [...values.galleryImages];
                      next[idx] = u;
                      setFieldValue("galleryImages", next);
                    }}
                    folder={folder}
                    label={`${t.admin.galleryImage} ${idx + 1}`}
                    testId={`gallery-image-${idx}`}
                  />
                ))}
              </div>
              {values.galleryImages.length < 15 && (
                <button
                  type="button"
                  onClick={() => setFieldValue("galleryImages", [...values.galleryImages, ""])}
                  style={{
                    marginTop: 8, padding: "6px 16px", fontSize: 13,
                    background: "transparent", border: "1px dashed #ccc",
                    borderRadius: 6, cursor: "pointer", color: "#666",
                  }}
                >
                  + Ajouter une image ({values.galleryImages.length}/15)
                </button>
              )}
              {values.galleryImages.length > 4 && (
                <p style={{ fontSize: 11, color: "#888", margin: "4px 0 0" }}>
                  Faites glisser les champs vides en fin de liste avant de soumettre.
                </p>
              )}
            </div>
            <AdminMediaAttachment
              attachmentType={values.attachmentType}
              attachmentUrl={values.attachmentUrl}
              attachmentLayout={values.attachmentLayout}
              folder={folder}
              setFieldValue={setFieldValue}
            />
            {type === "event" && (
              <>
                <AdminField label={t.admin.eventDate}>
                  <Field as={Input} name="eventDate" type="datetime-local" />
                </AdminField>
                <AdminField label={t.admin.location}>
                  <Field as={Input} name="location" />
                </AdminField>
              </>
            )}
            <AdminField label={t.admin.excerpt}>
              <Field as={Textarea} name="excerpt" rows={2} />
            </AdminField>
            <AdminField label={t.admin.contentBody} required error={touched.content ? errors.content : undefined}>
              <div data-testid="form-content">
                <RichEditor
                  value={values.content}
                  onChange={(html) => setFieldValue("content", html)}
                  folder={folder}
                />
              </div>
            </AdminField>
          </Form>
        </AdminFormDialog>
      )}
    </Formik>
  );
};
